import type { QueryRowType } from "@rocicorp/zero";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { useMemo, useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { config } from "../../environment";
import { useCreateSkill } from "../../hooks/useCreateSkill";
import { useCurrentUserGrants } from "../../hooks/useCurrentUserGrants";
import { useUserGroupMemberships } from "../../hooks/useUserGroupMemberships";
import { useHasAccess } from "../../hooks/useHasAccess";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../../hooks/useSpaceDisplayName";
import { useUrlSort } from "../../hooks/useUrlSort";
import { getWritableScopeIds } from "../../lib/getWritableScopeIds";
import { spaceIcon } from "../../lib/spaceIcon";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { usableSuppliersForSpace } from "../../lib/usableSuppliersForSpace";
import { userDisplayName } from "../../lib/userDisplayName";
import { AvailableToCell } from "../AvailableToCell";
import { Avatar } from "../Avatar";
import { Button } from "../Button";
import { ConfirmModal } from "../ConfirmModal";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { Dropdown } from "../Dropdown";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import {
  type FilterDef,
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
  parseMultiFilter,
  toggleMultiFilter,
} from "../ListFilter";
import { ListTable } from "../ListTable";
import { SkillUpdateReview } from "../SkillModal/SkillUpdateReview";
import { SkillAiAccessToggle } from "../SkillAiAccessToggle";
import { SkillUpdateStatus } from "../SkillUpdateStatus";
import { StatusPill, StatusPillCell } from "../StatusPill";
import { Text } from "../Text";
import { SkillsLayout } from "./SkillsLayout";
import { InstallGitSkillsModal } from "./InstallGitSkillsModal";

type SkillRow = QueryRowType<ReturnType<typeof queries.skillsManagementIndex>>;
type SkillCreator = NonNullable<SkillRow["createdBy"]>;

function SkillGitStatusCell({
  skill,
  canWrite,
}: {
  skill: SkillRow;
  canWrite: boolean;
}) {
  const z = useZero();
  const [reviewing, setReviewing] = useState(false);
  const installation = skill.gitInstallation;
  const source = installation?.source;
  const canUseConnector = useHasAccess(
    "connected_app:use",
    source?.connectedAppId ?? undefined
  );
  const canCheck =
    canWrite && source && (source.connectedAppId === null || canUseConnector);
  const automatic = !!(
    installation?.autoUpdate || installation?.autoUpdateRequested
  );
  const reviewAvailable =
    !!source && !!installation?.candidateHash && !automatic;
  return (
    <>
      <StatusPillCell>
        {installation ? (
          <SkillUpdateStatus
            installation={installation}
            inheritLink
            onReview={reviewAvailable ? () => setReviewing(true) : undefined}
            reviewDisabled={!canCheck}
            checkLabel={automatic ? "Update now" : "Check for updates"}
            onCheck={
              canCheck
                ? () =>
                    z.mutate(
                      sharedMutators.checkSkillUpdates({
                        skillId: skill.id,
                        connectedAppId: source.connectedAppId,
                      })
                    )
                : undefined
            }
          />
        ) : (
          <StatusPill
            color="gray"
            label="Local"
            detail="This skill isn’t linked to a Git repository."
            passive
            compactIcon="skills"
          />
        )}
      </StatusPillCell>
      {reviewing && source && (
        <SkillUpdateReview
          skillId={skill.id}
          connectedAppId={source.connectedAppId}
          onClose={() => setReviewing(false)}
        />
      )}
    </>
  );
}

export function Custom() {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const { canCreate, createSkill } = useCreateSkill();
  const openNewSkill = () => {
    const id = createSkill();
    if (id) navigate(routes.modal.skill, { id, tab: "files" });
  };
  const [skillsResult, skillsDetails] = useQuery(
    queries.skillsManagementIndex()
  );
  const [spaces] = useQuery(queries.spaces({ settingsView: true }));
  const { memberships: userGroupIds } = useUserGroupMemberships();
  const displaySpaceName = useSpaceDisplayName();
  const sharedSpaces = spaces.filter((space) => space.mode !== "personal");
  const usableSpacesBySkill = new Map<string, DisplayableSpace[]>();
  for (const skill of skillsResult) usableSpacesBySkill.set(skill.id, []);
  for (const space of sharedSpaces) {
    for (const skill of usableSuppliersForSpace(
      "space_uses_skills",
      space.roleGrantsAsScope,
      skillsResult,
      userGroupIds
    )) {
      usableSpacesBySkill.get(skill.id)?.push(space);
    }
  }
  const managedSkills = useMemo(
    () =>
      skillsResult.filter(
        (skill) => !skill.fresh || skill.gitInstallation !== undefined
      ),
    [skillsResult]
  );
  const {
    q: query,
    creator: creatorParam,
    space: spaceParam,
  } = useCurrentUrl(routes.skills.list);
  const creatorFilters = parseMultiFilter(creatorParam);
  const spaceFilters = parseMultiFilter(spaceParam);
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.skills.list,
    {
      name: "asc",
      addedBy: "asc",
    }
  );
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [installOpen, setInstallOpen] = useState(false);

  const [myGrants] = useCurrentUserGrants();
  const writableIds = useMemo(
    () =>
      getWritableScopeIds(
        myGrants,
        managedSkills.map((skill) => skill.id),
        "skill:write"
      ),
    [managedSkills, myGrants]
  );

  const queryFiltered = useMemo(() => {
    if (!query) return managedSkills;
    const lower = query.toLowerCase();
    return managedSkills.filter(
      (skill) =>
        skill.name.toLowerCase().includes(lower) ||
        skill.description.toLowerCase().includes(lower)
    );
  }, [managedSkills, query]);

  const availableCreators = new Map<string, SkillCreator>();
  const availableSpaceIds = new Set<string>();
  for (const skill of queryFiltered) {
    if (skill.createdByUserId && skill.createdBy) {
      availableCreators.set(skill.createdByUserId, skill.createdBy);
    }
    for (const space of usableSpacesBySkill.get(skill.id) ?? []) {
      availableSpaceIds.add(space.id);
    }
  }
  const availableCreatorOptions = [...availableCreators.values()].sort((a, b) =>
    userDisplayName(a).localeCompare(userDisplayName(b))
  );
  const availableSpaceOptions = sharedSpaces.filter((space) =>
    availableSpaceIds.has(space.id)
  );

  const skills = queryFiltered.filter((skill) => {
    if (creatorFilters.length > 0) {
      if (
        !skill.createdByUserId ||
        !creatorFilters.includes(skill.createdByUserId)
      ) {
        return false;
      }
    }
    if (spaceFilters.length > 0) {
      const usable = usableSpacesBySkill.get(skill.id);
      if (!usable?.some((space) => spaceFilters.includes(space.id)))
        return false;
    }
    return true;
  });

  const spaceFilter: FilterDef = {
    kind: "multi",
    key: "space",
    label: "Available to",
    icon: <Icon name="hash" size={16} />,
    options: availableSpaceOptions.map((space) => ({
      id: space.id,
      label: displaySpaceName(space),
      menuLeft: <Icon name={spaceIcon(space.mode)} size={16} />,
      pillLeft: <Icon name={spaceIcon(space.mode)} size={12} />,
    })),
    values: spaceFilters,
    onToggle: (id) =>
      navigate(
        null,
        { space: toggleMultiFilter(spaceFilters, id) },
        { replace: true }
      ),
  };

  const filters: FilterDef[] = [
    {
      kind: "multi",
      key: "creator",
      label: "Added by",
      icon: <Icon name="circle-user-round" size={16} />,
      options: availableCreatorOptions.map((creator) => ({
        id: creator.id,
        label: userDisplayName(creator),
        menuLeft: <Avatar user={creator} size={16} />,
        pillLeft: <Avatar user={creator} size={12} />,
      })),
      values: creatorFilters,
      onToggle: (id) =>
        navigate(
          null,
          { creator: toggleMultiFilter(creatorFilters, id) },
          { replace: true }
        ),
    },
    ...(availableSpaceOptions.length > 0 || spaceFilters.length > 0
      ? [spaceFilter]
      : []),
  ];

  const sortedSkills = [...skills].sort((a, b) => {
    if (!sortColumn) return b.createdAt - a.createdAt;
    let aVal: string | number;
    let bVal: string | number;
    switch (sortColumn) {
      case "name":
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
        break;
      case "addedBy":
        aVal = a.createdBy ? userDisplayName(a.createdBy).toLowerCase() : "";
        bVal = b.createdBy ? userDisplayName(b.createdBy).toLowerCase() : "";
        break;
    }
    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const toggleEnabled = (skill: SkillRow) => {
    z.mutate(
      sharedMutators.updateSkillSettings({
        skillId: skill.id,
        enabled: !(skill.enabled ?? true),
      })
    );
  };

  return (
    <>
      <title>{`Skills • ${config.title}`}</title>
      <SkillsLayout
        ready={skillsDetails.type === "complete"}
        searchAction={
          canCreate ? (
            <Dropdown
              opener={<Button size="l" icon="plus" />}
              minWidth={200}
              sections={[
                {
                  items: [
                    {
                      type: "button",
                      left: <Icon name="plus" size={16} />,
                      label: "Create from scratch",
                      onClick: openNewSkill,
                    },
                    {
                      type: "button",
                      left: <Icon name="git-branch" size={16} />,
                      label: "Install from Git",
                      onClick: () => setInstallOpen(true),
                    },
                  ],
                },
              ]}
            />
          ) : undefined
        }
      >
        <ListTable
          style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
          columns={{
            description: "flex",
            updateStatus: "md",
            availability: "content",
            space: "md",
          }}
          footer={
            hasActiveFilters(filters) ? (
              <ListFilterPills filters={filters} />
            ) : null
          }
          headerActions={<ListFilter filters={filters} />}
          emptyState={
            skillsDetails.type === "complete" ? (
              managedSkills.length > 0 ? (
                <EmptyState
                  iconName="skills"
                  title="No matches"
                  description="Try a different filter."
                />
              ) : !query ? (
                <EmptyState
                  iconName="skills"
                  title="No skills yet"
                  description={
                    canCreate
                      ? "Create a skill, or install one from a Git repository."
                      : "You don’t have permission to create skills. Ask someone who does to create one."
                  }
                />
              ) : undefined
            ) : undefined
          }
          headers={{
            name: "Skill",
            description: "Description",
            updateStatus: "Git status",
            space: "Available to",
            addedBy: "Added by",
            availability: "AI access",
            actions: "",
          }}
          sortableColumns={sortableColumns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          items={sortedSkills.map((skill) => ({
            key: skill.id,
            link: makeUrl(routes.modal.skill, { id: skill.id }),
            name: (hover) => (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  minWidth: 0,
                }}
              >
                <ConnectorLogoContainer
                  size={32}
                  hover={hover}
                  borderRadius={8}
                  outline={false}
                  hoverBackground={false}
                >
                  <Icon name="skills" size={16} />
                </ConnectorLogoContainer>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  <Text
                    ellipsis
                    variant="text-regular-md"
                    color="var(--text-primary)"
                  >
                    {skill.name || "Untitled skill"}
                  </Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    {`Created ${new Date(skill.createdAt).toLocaleDateString()}`}
                  </Text>
                </div>
              </div>
            ),
            description: (
              <Text
                variant="text-small"
                color="var(--text-secondary)"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {skill.description}
              </Text>
            ),
            updateStatus: (
              <SkillGitStatusCell
                skill={skill}
                canWrite={writableIds?.has(skill.id) ?? false}
              />
            ),
            availability: (
              <SkillAiAccessToggle
                enabled={skill.enabled ?? true}
                disabled={!writableIds?.has(skill.id) || skill.fresh}
                skillName={skill.name || "Untitled skill"}
                onToggle={() => toggleEnabled(skill)}
              />
            ),
            space: (
              <AvailableToCell
                spaces={usableSpacesBySkill.get(skill.id) ?? []}
                sharedSpaceCount={sharedSpaces.length}
                displaySpaceName={displaySpaceName}
              />
            ),
            addedBy: (
              <Text variant="text-small" color="var(--text-primary)" ellipsis>
                {skill.createdBy ? userDisplayName(skill.createdBy) : undefined}
              </Text>
            ),
            actions: (hasMouse) => {
              const canWrite = writableIds?.has(skill.id) ?? false;
              if (!canWrite) return null;
              return (
                <div style={{ opacity: hasMouse ? 1 : 0 }}>
                  <Dropdown
                    opener={<Button variant="secondary" icon="more" />}
                    minWidth={160}
                    sections={[
                      {
                        items: [
                          {
                            type: "button",
                            left: <Icon name="pencil" size={16} />,
                            label: "Edit",
                            onClick: () =>
                              navigate(routes.modal.skill, { id: skill.id }),
                          },
                          {
                            type: "button",
                            left: <Icon name="trash" size={16} />,
                            label: "Delete",
                            onClick: () => setConfirmDeleteId(skill.id),
                          },
                        ],
                      },
                    ]}
                  />
                </div>
              );
            },
          }))}
        />
      </SkillsLayout>
      <ConfirmModal
        open={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={() => {
          if (confirmDeleteId) {
            z.mutate(sharedMutators.deleteSkill({ skillId: confirmDeleteId }));
          }
          setConfirmDeleteId(null);
        }}
        title="Delete skill?"
        description="Permanently deletes this skill and its access settings."
        confirmLabel="Delete"
        destructive
      />
      {installOpen && (
        <InstallGitSkillsModal
          onClose={() => setInstallOpen(false)}
          onInstalled={() => setInstallOpen(false)}
        />
      )}
    </>
  );
}
